import React from 'react';
import type { TranscriptRevision } from '../types/portal';
import { formatEditStamp } from '../utils/time';
interface TranscriptVersionsProps {
  /** Every saved state, oldest first. The first is the original upload. */
  revisions: TranscriptRevision[];
  selectedId: string;
  onSelect: (id: string) => void;
}

/**
 * Who saved a version, said the way it actually happened.
 *
 * The stored `editorName` is either "Portal upload" (the studio delivering a
 * document), "Portal" / "Portal (Descript)" (the studio or its transcript
 * worker) or a client's own name. It was being printed raw next to the word
 * "Edit", which produced "Latest Edit — Portal upload" — three rows of it on
 * Bloomberg Ep3, none of which was an edit by anybody called Portal upload.
 */
function describeEditor(revision: TranscriptRevision, isFirst: boolean): string {
  const name = (revision.editorName ?? '').trim();
  const portal = /^portal/i.test(name);
  if (isFirst) return portal ? 'Original · uploaded by Portal' : `Original · from ${name}`;
  if (name.toLowerCase() === 'portal upload') return 'Replaced by Portal';
  if (portal) return 'Edited by Portal';
  return name ? `Edited by ${name}` : 'Edited';
}

/**
 * The saved states of a transcript, newest first.
 *
 * ONE VERSION IS NOT A HISTORY, so with a single saved state this renders
 * nothing at all rather than a list of one. That is deliberate: a client
 * opening a transcript that has never been touched should see the words,
 * not a panel telling them nothing has happened to the words.
 */
export function TranscriptVersions({
  revisions,
  selectedId,
  onSelect
}: TranscriptVersionsProps) {
  if (revisions.length < 2) return null;
  const rows = [...revisions].reverse();
  return <ul className="m-0 list-none border-t border-rule p-0" data-id="element-1842" data-mp="2s63sm51341yx">
      {rows.map((revision, index) => {
      const viewing = revision.id === selectedId;
      const isFirst = index === rows.length - 1;
      return <li key={revision.id} className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-rule py-4" data-id="element-1843" data-mp="xtw3j8bg2fb2" data-mpi={index}>
            <span className="label-sm text-charcoal" data-id="element-1844" data-mp="32tfcb27ns5ui">
              <span className={viewing ? 'text-ink' : 'text-mute'} data-id="element-1845" data-mp="36uqs6g0qdcpi">
                {describeEditor(revision, isFirst)}
              </span>
              {' · '}
              <span className="text-mute" data-id="element-1846" data-mp="3brh7v28mi6ae">{formatEditStamp(revision.at)}</span>
            </span>
            <button type="button" onClick={() => onSelect(revision.id)} aria-current={viewing} disabled={viewing} className={['label transition-colors duration-150 ease-out', viewing ? 'text-ink' : 'text-mute hover:text-ink'].join(' ')} data-id="element-1847" data-mp="3gserlj9a78e0">
              {viewing ? 'Viewing' : 'View'}
            </button>
          </li>;
    })}
    </ul>;
}